import { FormError } from '../../../../helpers/FormHelper';
import { ICookieConsentConfiguration } from '../../../../interfaces/CookieConsentConfiguration';
import { IGroupingRule } from '../../../../interfaces/GroupingRule';
import { ILoginConfiguration } from '../../../../interfaces/LoginConfiguration';
import { IProperty } from '../../../../interfaces/Property';
import { IScheduledJobConfiguration } from '../../../../interfaces/ScheduledJobConfiguration';
import { IUrlSeed } from '../../../../interfaces/UrlSeed';
import { IAllowedDeniedFilter } from '../../../../interfaces/UrlSetDiscoveryConfiguration';
import { SETTINGS_SECTION_TYPES } from '../../../../types/SettingsSectionTypes';
import { URL_SET_SOURCES } from '../../../../types/UrlSetSources';

export const ACTION_TYPES = {
	INIT_FROM_SERVER: 'INIT_FROM_SERVER',
	INIT_NEW: 'INIT_NEW',
	SET_NAME: 'SET_NAME',
	SET_SOURCE_MODE: 'SET_SOURCE_MODE',
	SET_SCHEDULING: 'SET_SCHEDULING',
	SET_LOGIN: 'SET_LOGIN',
	SET_SEEDS: 'SET_SEEDS',
	SET_SCOPE: 'SET_SCOPE',
	SET_GROUPING: 'SET_GROUPING',
	SET_MAX_PAGES: 'SET_MAX_PAGES',
	SET_FILE_TYPE: 'SET_FILE_TYPE',
	SET_ERRORS: 'SET_ERRORS',
	CLEAR_SECTION_ERROR: 'CLEAR_SECTION_ERROR',
	SET_LOADING: 'SET_LOADING',
	SET_SAVING: 'SET_SAVING',
	MARK_SAVED: 'MARK_SAVED',
	RESET_DRAFT: 'RESET_DRAFT',
	APPLY_SOURCE_CONVERSION: 'APPLY_SOURCE_CONVERSION',
	SET_DRAFT: 'SET_DRAFT'
} as const;

export interface PropertySettingsState {
	/** The working copy the user is actively editing. Null until loaded. */
	draft: IProperty | null;

	/** Deep clone of what was loaded from the server. Never mutated during editing.
	 *  Used for dirty comparison: isDirty = !isEqual(draft, original) */
	original: IProperty | null;

	/** Current source mode (list / discovery). Tracked separately because the user
	 *  can switch modes before saving, and the draft needs to be rebuilt on switch. */
	sourceMode: URL_SET_SOURCES | null;

	/** Validation errors from the last save attempt. Each error is tied to a
	 *  SETTINGS_SECTION_TYPES so sections can display their own errors. */
	errors: FormError[];

	/** True while the initial property fetch is in progress. */
	isLoading: boolean;

	/** True while a save/create operation is in progress. */
	isSaving: boolean;
}

export type PropertySettingsAction =
	| {
			type: typeof ACTION_TYPES.INIT_FROM_SERVER;
			payload: { property: IProperty; sourceMode: URL_SET_SOURCES };
	  }
	| { type: typeof ACTION_TYPES.INIT_NEW; payload: { property: IProperty } }
	| { type: typeof ACTION_TYPES.SET_NAME; payload: string }
	| { type: typeof ACTION_TYPES.SET_SOURCE_MODE; payload: URL_SET_SOURCES }
	| { type: typeof ACTION_TYPES.SET_SCHEDULING; payload: IScheduledJobConfiguration | undefined }
	| {
			type: typeof ACTION_TYPES.SET_LOGIN;
			payload: {
				scanLogin: ILoginConfiguration | null;
				urlSetLogin: ILoginConfiguration | null;
				cookieConsent: ICookieConsentConfiguration | null;
			};
	  }
	| { type: typeof ACTION_TYPES.SET_SEEDS; payload: { seeds?: IUrlSeed[]; urls?: string[] } }
	| {
			type: typeof ACTION_TYPES.SET_SCOPE;
			payload: {
				languageFilter: IAllowedDeniedFilter;
				urlPatternFilter: IAllowedDeniedFilter;
				domainFilter: IAllowedDeniedFilter;
			};
	  }
	| { type: typeof ACTION_TYPES.SET_GROUPING; payload: IGroupingRule[] }
	| { type: typeof ACTION_TYPES.SET_MAX_PAGES; payload: number }
	| { type: typeof ACTION_TYPES.SET_FILE_TYPE; payload: string[] }
	| { type: typeof ACTION_TYPES.SET_ERRORS; payload: FormError[] }
	| { type: typeof ACTION_TYPES.CLEAR_SECTION_ERROR; payload: SETTINGS_SECTION_TYPES }
	| { type: typeof ACTION_TYPES.SET_LOADING; payload: boolean }
	| { type: typeof ACTION_TYPES.SET_SAVING; payload: boolean }
	| { type: typeof ACTION_TYPES.MARK_SAVED }
	| { type: typeof ACTION_TYPES.RESET_DRAFT }
	| { type: typeof ACTION_TYPES.APPLY_SOURCE_CONVERSION; payload: IProperty }
	| { type: typeof ACTION_TYPES.SET_DRAFT; payload: IProperty | ((prev: IProperty) => IProperty) };

export const INITIAL_STATE: PropertySettingsState = {
	draft: null,
	original: null,
	sourceMode: null,
	errors: [],
	isLoading: true,
	isSaving: false
};
